import {
	type NodeViewComponentProps,
	useEditorEventCallback,
	useSelectNode,
	useStopEvent,
} from "@handlewithcare/react-prosemirror";
import composeRefs from "@seznam/compose-react-refs";
import cx from "classnames";
import { Logger } from "logging";
import { normalize } from "pathe";
import { NodeSelection } from "prosemirror-state";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useAsync } from "react-use";

import {
	AlignHorizontalCenterIcon,
	AlignHorizontalLeftIcon,
	AlignHorizontalRightIcon,
} from "@moment/icons";
import {
	env,
	useAuth,
	useDocumentFromCurrentRoute,
	usePageFromCurrentRoute,
	useRelatedFileContents,
	useWorkspaceFromCurrentRoute,
} from "@moment/state";
import { MomentBasicTooltip as BasicTooltip } from "@moment/ui";

import { readAsDataURL } from "~/components/canvas/easel/prosemirror/plugins/imageDropPlugin";

const logger = new Logger("Image");

export const Image = forwardRef<HTMLDivElement, NodeViewComponentProps>(function Image(
	{ nodeProps, children: _, ...props },
	ref
) {
	const { node, getPos } = nodeProps;
	const innerRef = useRef<HTMLDivElement | null>(null);
	const initialWidth = node.attrs["width"] as number;
	const aspectRatio = node.attrs["aspectRatio"] as number;
	const initialHeight = initialWidth * (1 / aspectRatio);

	const { workspaceName } = useWorkspaceFromCurrentRoute();
	const { documentId, isEditable } = useDocumentFromCurrentRoute();
	const { pageId } = usePageFromCurrentRoute();
	const { callback: getRelatedFileContents, isLoading, isError } = useRelatedFileContents();
	const { getAccessTokenSilently } = useAuth();

	let filename = node.attrs["file"] as string | undefined;
	const src = node.attrs["src"] as string | undefined;

	if (src && !filename) {
		filename = filename ?? normalize(src).split("/").pop();
	}

	const {
		value: dataUrl,
		loading: dataUrlLoading,
		error: dataUrlError,
	} = useAsync(async () => {
		// Need either a filename (for related/ folder) or a src (for document-relative path)
		if (!filename && !src) {
			return null;
		}

		// Don't try to load if required route params are not yet available
		if (!workspaceName || !documentId || !pageId) {
			return null;
		}

		try {
			// Get the access token from the auth hook (handles iframe token persistence)
			const token = await getAccessTokenSilently.getAccessTokenSilently?.();
			const fileContents = await getRelatedFileContents({
				workspaceName,
				documentId,
				pageId,
				filename,
				src,
				token,
			});

			const url = await readAsDataURL(new Uint8Array(fileContents).buffer, src ?? filename);
			return url;
		} catch (err) {
			logger.error("Failed to load image", { cause: err });
			throw err;
		}
	}, [
		getRelatedFileContents,
		getAccessTokenSilently,
		workspaceName,
		documentId,
		pageId,
		filename,
		src,
	]);

	const [width, setWidth] = useState(initialWidth);
	const [isSelected, setIsSelected] = useState(false);

	const dragStartRef = useRef<null | { x: number; y: number }>(null);
	const dragDirectionRef = useRef<null | "left" | "right" | "down">(null);

	useSelectNode(
		() => {
			setIsSelected(true);
		},
		() => {
			setIsSelected(false);
		}
	);

	useEffect(() => {
		function onMouseMove(event: MouseEvent) {
			if (!dragStartRef.current || !dragDirectionRef.current || !innerRef.current) {
				return;
			}

			let diff = 0;
			switch (dragDirectionRef.current) {
				case "down": {
					diff = event.clientY - dragStartRef.current.y;
					break;
				}
				case "left": {
					diff =
						node.attrs["alignment"] === "center"
							? (dragStartRef.current.x - event.clientX) * 2
							: dragStartRef.current.x - event.clientX;
					break;
				}
				case "right": {
					diff =
						node.attrs["alignment"] === "center"
							? (event.clientX - dragStartRef.current.x) * 2
							: event.clientX - dragStartRef.current.x;
					break;
				}
			}

			const style = getComputedStyle(innerRef.current);
			const defaultPadding = style.getPropertyValue("--default-padding");
			const numRem = parseFloat(defaultPadding.replace("rem", ""));
			const padding = numRem * parseFloat(style.fontSize);
			const parentWidth = innerRef.current.offsetWidth - padding * 2;
			if (dragDirectionRef.current === "left" || dragDirectionRef.current === "right") {
				setWidth(Math.min(parentWidth, initialWidth + diff));
			} else {
				setWidth(Math.min(parentWidth, (initialHeight + diff) * aspectRatio));
			}
		}

		document.addEventListener("mousemove", onMouseMove);
		return () => {
			document.removeEventListener("mousemove", onMouseMove);
		};
	}, [aspectRatio, initialHeight, initialWidth, node.attrs]);

	const onMouseUp = useEditorEventCallback((view) => {
		if (!dragStartRef.current && !dragDirectionRef.current) {
			return false;
		}
		dragStartRef.current = null;
		dragDirectionRef.current = null;
		const tr = view.state.tr;
		tr.setNodeAttribute(getPos(), "width", width);
		view.dispatch(tr);
		return true;
	});

	useEffect(() => {
		document.addEventListener("mouseup", onMouseUp);
		return () => {
			document.removeEventListener("mouseup", onMouseUp);
		};
	}, [onMouseUp]);

	useStopEvent((_view, event) => {
		if (event instanceof DragEvent) {
			event.preventDefault();
			return true;
		}
		return false;
	});

	const onClick = useEditorEventCallback((view) => {
		view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, getPos())));
	});

	const onAlignmentClick = useEditorEventCallback(
		(view, alignment: "left" | "center" | "right") => {
			view.dispatch(view.state.tr.setNodeAttribute(getPos(), "alignment", alignment));
		}
	);

	return (
		<div
			data-text-cell
			ref={composeRefs(ref, innerRef)}
			{...props}
			className={cx("flex relative", {
				"justify-start": node.attrs["alignment"] === "left",
				"justify-center": node.attrs["alignment"] === "center",
				"justify-end": node.attrs["alignment"] === "right",
			})}
		>
			{}
			{isError || dataUrlError ? (
				<div
					className="flex items-center justify-center bg-primary bg-opacity-5 m-8"
					style={{
						width,
						height: width / aspectRatio,
					}}
				>
					Failed to load image
				</div>
			) : isLoading ||
			  dataUrlLoading ||
			  ((filename || src) && (!workspaceName || !documentId || !pageId || !dataUrl)) ? (
				<div
					className="relative overflow-hidden rounded-md bg-muted m-8"
					style={{
						width,
						height: width / aspectRatio,
					}}
				>
					<div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/10 to-transparent" />
				</div>
			) : (
				<>
					{isSelected && isEditable && (
						<div
							className="absolute select-none bottom-8 left-1/2 transform -translate-x-1/2 z-popover bg-brand mb-2 gap-2 p-1 flex justify-between rounded-lg text-fixed-light-default"
							contentEditable={false}
						>
							<BasicTooltip content="Align left" triggerProps={{ asChild: true }}>
								<button
									className={cx(
										"w-8 h-8 rounded hover:bg-brand-light flex items-center justify-center",
										{
											"bg-brand-light": node.attrs["alignment"] === "left",
										}
									)}
									onClick={() => onAlignmentClick("left")}
								>
									<AlignHorizontalLeftIcon />
								</button>
							</BasicTooltip>
							<BasicTooltip content="Align left" triggerProps={{ asChild: true }}>
								<button
									className={cx(
										"w-8 h-8 rounded hover:bg-brand-light flex items-center justify-center",
										{
											"bg-brand-light": node.attrs["alignment"] === "center",
										}
									)}
									onClick={() => onAlignmentClick("center")}
								>
									<AlignHorizontalCenterIcon />
								</button>
							</BasicTooltip>
							<BasicTooltip content="Align left" triggerProps={{ asChild: true }}>
								<button
									className={cx(
										"w-8 h-8 rounded hover:bg-brand-light flex items-center justify-center",
										{
											"bg-brand-light": node.attrs["alignment"] === "right",
										}
									)}
									onClick={() => onAlignmentClick("right")}
								>
									<AlignHorizontalRightIcon />
								</button>
							</BasicTooltip>
						</div>
					)}
					<div className={cx("pb-4 group/image-resize-overlay relative w-max flex")}>
						{isEditable && (
							<>
								{node.attrs["alignment"] !== "left" && (
									<button
										onMouseDown={(e) => {
											dragStartRef.current = { x: e.clientX, y: e.clientY };
											dragDirectionRef.current = "left";
										}}
										className=" group-hover/image-resize-overlay:flex hidden items-center justify-center absolute top-1/2 left-6 transform -translate-y-1/2 w-4 h-24 "
									>
										<div className="w-2 h-16 bg-brand-light border border-solid rounded-md border-brand-foreground" />
									</button>
								)}
								<button
									onMouseDown={(e) => {
										dragStartRef.current = { x: e.clientX, y: e.clientY };
										dragDirectionRef.current = "down";
									}}
									className=" group-hover/image-resize-overlay:flex hidden items-center justify-center absolute bottom-6 left-1/2 transform -translate-x-1/2 w-24 h-4 "
								>
									<div className="w-16 h-2 bg-brand-light border border-solid rounded-md border-brand-foreground" />
								</button>
								{node.attrs["alignment"] !== "right" && (
									<button
										onMouseDown={(e) => {
											dragStartRef.current = { x: e.clientX, y: e.clientY };
											dragDirectionRef.current = "right";
										}}
										className=" group-hover/image-resize-overlay:flex hidden items-center justify-center absolute top-1/2 right-6 transform -translate-y-1/2 w-4 h-24 "
									>
										<div className="w-2 h-16 bg-brand-light border border-solid rounded-md border-brand-foreground" />
									</button>
								)}
							</>
						)}

						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img
							data-file={node.attrs["file"]}
							width={width}
							src={dataUrl ?? undefined}
							alt=""
							onClick={onClick}
							className={cx(
								isEditable && "border-4 border-solid",
								isSelected ? "border-brand" : "border-transparent",
								!env.isDesktopApp ? "max-w-full h-auto" : ""
							)}
						/>
					</div>
				</>
			)}
		</div>
	);
});
